import { useCallback, useEffect, useState } from "react";
import {
  ContentWrapper,
  FullWidthHorizontalSeparator,
  PageHeader,
  PageTitle,
  PageWrapper,
} from "../../../../../layout/portalPageLayout";
import {
  HeaderLeft,
  HeaderRight,
} from "../../../../../components/Layout/Layout";
import { DestructiveButtonSmallV2 } from "../../../../../components/Buttons/Buttons";
import { useTranslation } from "react-i18next";
import { CreateNewProductPage } from "./CreateNewProductPage";
import { CreateFromExistingProductPage } from "./CreateFromExistingProductPage";
import { useHistory } from "react-router-dom";
import type { FormSource } from "./CreatePimProduct.utils";
import { NumberParam, useQueryParams } from "use-query-params";
import { useRoutePath } from "../../../../../util/Routing";
import { TabPanels, Tabs } from "@reach/tabs";
import { LazyTabPanel } from "../../../../../components/ReachTabs/TabPanel";
import { TabList } from "../../../../../components/ReachTabs/TabList";
import { Tab } from "../../../../../components/ReachTabs/Tab";
import { CreatePimProductFromUploads } from "./CreateFromUploads/CreatePimProductFromUpload";
import { useStoreState } from "../../../../../util/util";
import { CreateNewProductViaFormPage } from "./CreateNewProductViaForm";
import { RequestTrackingPage } from "./RequestTrackingPage";
import { useAuthContext } from "../../../../../components/Auth";

export const CreatePimProductPage = () => {
  const [formSource, setFormSource] = useState<FormSource>("new");
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [query, setQuery] = useQueryParams({ tab: NumberParam });
  const {
    storefront_metadata: {
      only_bulk_upload_enabled,
      enable_product_creation_via_form,
    },
  } = useStoreState();

  const { user } = useAuthContext();

  const { t } = useTranslation();
  const { adminPath } = useRoutePath();
  const history = useHistory();

  /**
   * Despite the naming here in reality for every storefront except TER all 3 tabs will show up,
   * perhaps one day there will be 3 separate product creation RBAC permissions
   */

  const goToTabByKey = (key: string) => {
    const index = TabsList.findIndex((tab) => tab.key === key);
    if (index !== -1) {
      changeTab(index);
    }
  };

  const isTabSelected = (key: string) =>
    currentTab === TabsList.findIndex((tab) => tab.key === key);

  const tabsThatAlwaysExist = [
    <Tab key="product_upload">{t("Bulk Upload")}</Tab>,
  ];

  const optionalTabs = [
    <Tab key="new_product">{t("New Product")}</Tab>,
    <Tab key="existing_product">{t("Copy Existing Product")}</Tab>,
  ];

  const TabsList = [
    ...(!only_bulk_upload_enabled &&
    (!enable_product_creation_via_form || user?.rbac_role === "agilis_admin")
      ? optionalTabs
      : []),
    ...(!enable_product_creation_via_form || user?.rbac_role === "agilis_admin"
      ? tabsThatAlwaysExist
      : []),
    ...(enable_product_creation_via_form
      ? [
          <Tab key="new_product_via_form">{t("New Product via Form")}</Tab>,
          <Tab key="request_tracking">{t("Request Tracking")}</Tab>,
        ]
      : []),
  ];

  const TabPanelListAlwaysExists = [
    <LazyTabPanel
      key="product_upload"
      refreshKey={currentTab}
      isSelected={isTabSelected("product_upload")}
    >
      <CreatePimProductFromUploads />
    </LazyTabPanel>,
  ];

  const TabPanelListOptional = [
    <LazyTabPanel
      key="new_product"
      refreshKey={currentTab}
      isSelected={isTabSelected("new_product")}
    >
      <CreateNewProductPage formSource={formSource} />
    </LazyTabPanel>,
    <LazyTabPanel
      key="existing_product"
      refreshKey={currentTab}
      isSelected={isTabSelected("existing_product")}
    >
      <CreateFromExistingProductPage formSource={formSource} />
    </LazyTabPanel>,
  ];

  const TabPanelList = [
    ...(!only_bulk_upload_enabled &&
    (!enable_product_creation_via_form || user?.rbac_role === "agilis_admin")
      ? TabPanelListOptional
      : []),
    ...(!enable_product_creation_via_form || user?.rbac_role === "agilis_admin"
      ? TabPanelListAlwaysExists
      : []),
    ...(enable_product_creation_via_form
      ? [
          <LazyTabPanel
            key="new_product_via_form"
            refreshKey={currentTab}
            isSelected={isTabSelected("new_product_via_form")}
          >
            <CreateNewProductViaFormPage
              formSource={formSource}
              goToTabByKey={goToTabByKey}
            />
          </LazyTabPanel>,
          <LazyTabPanel
            key="request_tracking"
            refreshKey={currentTab}
            isSelected={isTabSelected("request_tracking")}
          >
            <RequestTrackingPage />
          </LazyTabPanel>,
        ]
      : []),
  ];

  const changeTab = useCallback(
    (index: number) => {
      setCurrentTab(index);
      setQuery({ tab: index });
      if (index < 2) {
        setFormSource(index === 0 ? "new" : "existing");
      }
    },
    [setQuery]
  );

  useEffect(() => {
    if (query.tab) {
      changeTab(query.tab);
    }
  }, [changeTab, query]);

  return (
    <PageWrapper>
      <PageHeader>
        <HeaderLeft>
          <PageTitle>{t("Create Product")}</PageTitle>
        </HeaderLeft>
        <HeaderRight>
          <DestructiveButtonSmallV2
            onClick={() => history.push(`${adminPath}/pim/products`)}
          >
            {t("Cancel")}
          </DestructiveButtonSmallV2>
        </HeaderRight>
      </PageHeader>
      <ContentWrapper>
        <Tabs onChange={changeTab} index={currentTab}>
          <TabList>{TabsList}</TabList>
          <FullWidthHorizontalSeparator />
          <TabPanels>{TabPanelList}</TabPanels>
        </Tabs>
      </ContentWrapper>
    </PageWrapper>
  );
};
